import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import category from "../../assets/images/icons/category.svg";
import dental from "../../assets/images/icons/dental-braces.svg";
import doctor from "../../assets/images/icons/doctor-01.svg";
import FilterIcon from "../../assets/images/icons/filter.svg";
import Labs from "../../assets/images/icons/Labs.svg";
import mapView from "../../assets/images/icons/mapView.svg";
import Pharmacies from "../../assets/images/icons/Pharmacies.svg";
import Radiology from "../../assets/images/icons/Radiology.svg";
import { 
  GeneralButton,
  InfiniteScroll,
  NoResult,
  SearchComponent,
} from "../../components";
import { EndPoints } from "../../Constants/endpoints";
import { useAppMediaQuery, useGetData } from "../../hooks";
import {
  IFacilityType,
  IServiceProvider,
  IServiceProviderGrouped,
} from "../../interface/ProfileInterfaces";
import { ClosableTag, TitleSideLink, TitleWithArrow } from "../../Share";
import {
  DoctorsLoader,
  GeneralServiceProviderFilter,
  ServiceProviderCard,
  ServiceProvidersMap,
} from "./component";

const GeneralSearch: React.FC = () => {
  const { data: facilityTypesData } = useGetData<IFacilityType[]>(
    EndPoints.GetFacilityTypes,
  );

  const { t } = useTranslation();
  const [tags, setTags] = useState<any>([]);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isMapView, setIsMapView] = useState<boolean>(false);
  const [searchProviderResultsData, setSearchProviderResultsData] =
    useState<IServiceProvider[]>();

  const location = useLocation();
  const navigate = useNavigate();
  const currentLocationParams = new URLSearchParams(location.search);
  const TypeId = currentLocationParams.get("facility") ?? "All";

  const isPatient = TypeId === "Patients";

  const [paginationParams, setPaginationParams] = useState<any>({
    Start: 0,
    Count: 15,
    currentPage: 0,
  });

  const facilityTypes = facilityTypesData?.Data ?? [];
  const { isMobileOrTablet } = useAppMediaQuery();
  const searchValue = currentLocationParams.get("search-value");
  const allParams = Object.fromEntries(currentLocationParams.entries());

  const TypeCode = facilityTypes?.find((item: any) => item?.Id == TypeId)?.Code;

  const AccountTypeId = facilityTypes?.find((item: any) => TypeId == item?.Id)
    ?.AccountType?.Id;

  const filtersParams: any = {
    Name: searchValue ?? undefined,
    ...(TypeId !== "All" && !isPatient && { TypeId: Number(TypeId) }),
    ...allParams,
  };

  Object.keys(filtersParams).forEach((key) =>
    filtersParams[key] === undefined || filtersParams[key] === null
      ? delete filtersParams[key]
      : {},
  );

  useEffect(() => {
    setPaginationParams((prev: any) => ({
      ...prev,
      ...(TypeId !== "All" && !isPatient && { TypeId: Number(TypeId) }),
    }));
  }, [TypeId]);

  const { data, isLoading } = useGetData<IServiceProvider[]>(
    EndPoints.ServiceProvider,
    { filter: { ...paginationParams, ...filtersParams } },
  );

  const { data: GroupedData } = useGetData<IServiceProviderGrouped[]>(
    EndPoints.ServiceProviderGroupedSearch,
    { filter: TypeId === "All" ? { Count: 3, ...filtersParams } : undefined },
  );

  const methods = useForm({
    defaultValues: allParams,
  });
  const { reset } = methods;

  const deleteParams = (paramsToDelete: string[]) => {
    const newParams = new URLSearchParams(location.search);
    paramsToDelete.forEach((param) => {
      newParams.delete(param);
    });
    return newParams.toString();
  };

  const filterByFacility = currentLocationParams.get("facility") ?? "All";

  const handleFormSubmit = (data: any) => {
    Object.keys(data).forEach((key) => {
      if (!data[key] || (key == "PriceEnd" && data[key] == 200000)) {
        currentLocationParams.delete(key);
      } else {
        if (key == "facility")
          if (data[key] !== "All" && data[key] !== "Patients") {
            currentLocationParams.set(key, filterByFacility);
          } else {
            currentLocationParams.delete(key);
          }
        else currentLocationParams.set(key, data[key]);
      }
    });

    const queryString = currentLocationParams.toString();
    navigate(`?${queryString}`);

    setIsFilterOpen(false);
    setIsMapView(false);
  };

  const serviceProvidersForMap = data?.Data || [];

  const getIconForFacility = (item: any) => {
    if (
      item?.Id === "All" ||
      item?.Name === 't("all")' ||
      item?.Name === "all"
    ) {
      return category;
    }

    const nameToCheck = (item?.NameEn || item?.Name)?.toLowerCase()?.trim();

    switch (nameToCheck) {
      case "doctors":
      case "doctor":
        return doctor;
      case "dentists":
      case "dentist":
        return dental;
      case "radiology":
      case "radiology center":
        return Radiology;
      case "laboratories":
      case "labs":
        return Labs;
      case "pharmacies":
      case "pharmacy":
        return Pharmacies;
      default:
        return category;
    }
  };
  return (
    <div className="box flex h-fit min-h-full grow flex-col gap-2 overflow-hidden xl:h-full">
      <div className="flex w-full flex-wrap items-center justify-between">
        <TitleWithArrow title="search_care_providers" />
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-96 xl:w-[440px]">
            <SearchComponent
              placeholder={t("search_doctors_pharmacies_dentists_etc")}
              showSearchHistory
              value={searchValue && searchValue?.length > 0 ? searchValue : ""}
              onSearch={(searchTerm: string) => {
                const currentParams = new URLSearchParams(location.search);
                if (searchTerm && searchTerm?.length > 0) {
                  currentParams.set("search-value", searchTerm);
                } else {
                  currentParams.delete("search-value");
                }
                navigate(`/search?${currentParams.toString()}`);
              }}
            />
          </div>
          {TypeId != "All" && TypeId != "Patients" && (
            <>
              <GeneralButton
                color="main"
                title={!isMobileOrTablet ? t("filter") : ""}
                icon={FilterIcon}
                onClick={() => {
                  setIsFilterOpen(true);
                }}
                isActive={isFilterOpen}
              />
              <GeneralButton
                color="main"
                title={
                  !isMobileOrTablet
                    ? isMapView
                      ? t("list_view")
                      : t("map_view")
                    : ""
                }
                icon={mapView}
                isActive={isMapView}
                onClick={() => setIsMapView((prev) => !prev)}
              />
            </>
          )}
        </div>
      </div>
      <div
        className={`flex h-full w-full gap-4 lg:gap-10 ${
          isMobileOrTablet ? "flex-col" : ""
        }`}
      >
        <div
          className={`flex h-fit gap-2 overflow-auto max-sm:overflow-x-auto sm:w-full sm:flex-row lg:!w-56 lg:flex-col`}
        >
          {[{ Name: t("all"), Id: "All" }, ...facilityTypes]?.map(
            (item, index: number) => {
              const isSelected = TypeId == item?.Id;
              return isMobileOrTablet ? (
                <div
                  className={`h-fit cursor-pointer text-nowrap rounded-lg p-3 max-lg:text-center lg:w-52 lg:text-lg ${
                    isSelected
                      ? "font-bold text-primary lg:bg-primary-bg"
                      : "text-body-text"
                  }`}
                  key={index}
                  onClick={() => {
                    const newParams = new URLSearchParams();
                    newParams.set("facility", `${item?.Id}`);
                    searchValue &&
                      newParams.set("search-value", `${searchValue}`);
                    const queryString = newParams.toString();
                    reset(undefined);
                    navigate(`?${queryString}`);
                    setIsMapView(false);
                  }}
                >
                  {item?.Name}
                </div>
              ) : (
                <TitleSideLink
                  key={index}
                  label={item?.Name}
                  icon={getIconForFacility(item)}
                  isActive={isSelected}
                  onClick={() => {
                    const newParams = new URLSearchParams();
                    newParams.set("facility", `${item?.Id}`);
                    searchValue &&
                      newParams.set("search-value", `${searchValue}`);
                    const queryString = newParams.toString();
                    reset(undefined);
                    navigate(`?${queryString}`);
                    setIsMapView(false);
                  }}
                />
              );
            },
          )}
        </div>
        <div className="relative flex-1 overflow-auto">
          <h2 className="text-lg">{t("results")}</h2> 
           
          {tags?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 !px-4 py-2.5">
              {tags?.map((item: any, index: number) => (
                <ClosableTag
                  key={index}
                  title={item?.label}
                  onClose={() => {
                    const updatedParams = deleteParams([item?.key]);
                    reset(
                      Object.fromEntries(new URLSearchParams(updatedParams)),
                    );
                    navigate(`?${updatedParams}`);
                    setIsMapView(false);
                  }}
                />
              ))}
            </div>
          )}
          {isLoading ? (
            <div
              className={`grid max-h-[85%] w-full auto-rows-max gap-x-8 gap-y-8 overflow-y-auto sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-2`}
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <DoctorsLoader key={index} />
              ))}
            </div>
          ) : isMapView ? (
            <ServiceProvidersMap items={serviceProvidersForMap} />
          ) : TypeId === "All" ? (
            GroupedData?.TotalCount == 0 ? (
              <NoResult title="make_sure_the_entered_name_is_spelled_correctly" />
            ) : (
              <>
                <div
                  className={`grid max-h-full w-full auto-rows-max gap-x-8 gap-y-8 overflow-auto pt-2 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-1 lg:pt-0 xl:grid-cols-2`}
                >
                  {GroupedData?.Data?.map(
                    (item: IServiceProviderGrouped | any, index: number) => (
                      <div
                        key={index}
                        className="flex flex-col gap-2 border-b border-b-primary-bg"
                      >
                        <h4 className="text-sm font-bold">
                          {item?.FacilityType?.Name}
                        </h4>
                        {item?.Providers?.map(
                          (provider: IServiceProviderGrouped, key: number) => (
                            <ServiceProviderCard item={provider} key={key} />
                          ),
                        )}
                        <GeneralButton
                          color="liteGray"
                          title={t("see_all")}
                          styleClasses="w-full"
                          onClick={() => {
                            const newParams = new URLSearchParams(
                              currentLocationParams,
                            );
                            newParams.set("facility", item?.FacilityType?.Id);
                            const queryString = newParams.toString();
                            reset(undefined);
                            navigate(`?${queryString}`);
                            setIsMapView(false);
                          }}
                        />
                      </div>
                    ),
                  )}
                </div>
              </>
            )
          ) : (
            <>
              <div
                className={`grid max-h-[85%] w-full flex-1 grid-cols-1 gap-x-10 gap-y-2 overflow-y-auto md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-2 ${(searchProviderResultsData?.length ?? 0) > 0 || !searchProviderResultsData ? "auto-rows-max" : ""}`}
              >
                {searchProviderResultsData?.map(
                  (item: IServiceProvider, index: number) => (
                    <ServiceProviderCard item={item} key={index} />
                  ),
                )}
                <InfiniteScroll
                  Count={10}
                  skeletonCount={2}
                  endpoint={EndPoints.ServiceProvider}
                  getAllParams={{ ...paginationParams, ...filtersParams }}
                  getData={(data) => setSearchProviderResultsData(data)}
                  renderSkeleton={
                    <div className="ps-3">
                      <DoctorsLoader />
                    </div>
                  }
                  noDataElement={
                    <NoResult title="make_sure_the_entered_name_is_spelled_correctly" />
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>

      <GeneralServiceProviderFilter
        TypeId={TypeId}
        AccountTypeId={AccountTypeId}
        title={
          TypeCode === "DENTALCLINIC"
            ? t("dentist_filters")
            : TypeCode == "RADIOLOGYCENTER"
              ? t("radiology_filters")
              : TypeCode == "MEDIACLlABRATORY"
                ? t("laboratories_filters")
                : t("doctor_filters")
        }
        setTags={setTags}
        methods={methods}
        onReset={() => {
          const updatedParams = deleteParams([
            "SpecilaityId",
            "CityId",
            "AreaId",
            "Gender",
            "PriceEnd",
            "PriceSet",
          ]);

          reset(Object.fromEntries(new URLSearchParams(updatedParams)));
          navigate(`?${updatedParams}`);
          setIsMapView(false);
        }}
        onSubmit={handleFormSubmit}
        isOpen={isFilterOpen}
        onClose={() => {
          setIsFilterOpen(false);
        }}
      />
    </div>
  );
};

export default GeneralSearch;
